<template>
	<div>
		<upload-header :title="$t(`upload.image-manager.title-${quantityLabel}`)" />

		<error-message
			:errors="errors"
			@errorsClosed="$emit('errorsClosed')"
		/>

		<div class="upload__content">
			<upload-box
				type="vertical"
				alignment="left"
			>
				<helio-tabs
					:full-width="true"
					class="upload__tabs"
					@changed="changedTab"
				>
					<helio-tab
						v-if="uploader"
						id="uploader"
						:name="$t('upload.image-manager.upload')"
						:active="openedTab === 'uploader' || (!openedTab && uploader)"
						class="upload__tab upload__tab--uploader"
					>
						<upload-uploader
							:maximum-size="maximumSize"
							:minimum-width="minimumWidth"
							:minimum-height="minimumHeight"
							:extensions="extensions"
							:api="api"
							@hasError="handleErrors"
						/>
					</helio-tab>

					<helio-tab
						v-if="unsplash && unsplashSecret && unsplashAppId"
						id="unsplash"
						:name="$t('upload.image-manager.search')"
						:active="openedTab === 'unsplash' || (!openedTab && !uploader && unsplash)"
						class="upload__tab upload__tab--unsplash scroll"
					>
						<upload-unsplash
							:secret="unsplashSecret"
							:app-id="unsplashAppId"
							:last-search-term="lastSearchTerm"
							@changed-search="$emit('changed-search', $event)"
						/>
					</helio-tab>
				</helio-tabs>
			</upload-box>

			<upload-box
				type="vertical"
				alignment="right"
			>
				<upload-heading :title="galleryTitle" />

				<upload-grid
					v-if="medias.length > 0"
					ref="grid"
					@scroll.native="debouncedScroll"
				>
					<upload-thumbnail
						v-for="(media, index) in visibleMedias"
						:key="index"
						:media="media"
						:selected="selection.includes(media.id) || media.selected"
						:can-be-selected="canBeSelected()"
						:api="api"
						type="image"
						extra="creationDate"
						@toggle="$emit('toggle', media.id)"
						@preview="$emit('preview', media)"
						@delete="$emit('delete', $event)"
					/>
				</upload-grid>

				<helio-empty
					v-if="medias.length === 0"
					:initial-title="$t('upload.image-manager.gallery.empty.title')"
					:initial-subtitle="$t('upload.image-manager.gallery.empty.subtitle')"
					illustration="feather"
				/>
			</upload-box>

			<upload-box
				v-if="orderable && quantity !== 1"
				type="horizontal"
			>
				<upload-heading :title="$t('upload.orderable.title')" />

				<upload-orderable
					:selection="selection"
					:medias="medias"
					:quantity="quantity"
					:minimum-files="minimumFiles"
					:maximum-files="maximumFiles"
					@order-changed="$emit('order-changed', $event)"
				/>
			</upload-box>
		</div>

		<upload-footer>
			<helio-button
				:text="$t('upload.image-manager.cancel')"
				@click.stop="$emit('close')"
			/>

			<helio-button
				:disabled="canBeSaved()"
				:text="$t('upload.image-manager.save')"
				color="primary"
				data-cy="save-image"
				@click.stop="$emit('save')"
			/>
		</upload-footer>
	</div>
</template>

<script>
import _orderBy from 'lodash/orderBy';
import _debounce from 'lodash/debounce';
import Tabs from '../../../tabs/Tabs.vue';
import Tab from '../../../tab/Tab.vue';
import Empty from '../../../empty/Empty.vue';
import Button from '../../../button/Button.vue';
import Uploader from '../../uploader/Uploader.vue';
import Unsplash from '../../unsplash/Unsplash.vue';
import Orderable from '../../orderable/Orderable.vue';
import Footer from '../../footer/Footer.vue';
import Header from '../../header/Header.vue';
import Heading from '../../heading/Heading.vue';
import Thumbnail from '../../thumbnail/Thumbnail.vue';
import Box from '../../box/Box.vue';
import Grid from '../../grid/Grid.vue';
import ErrorMessage from '../../error-message/ErrorMessage.vue';

export default {
	components: {
		'helio-tabs': Tabs,
		'helio-tab': Tab,
		'helio-empty': Empty,
		'helio-button': Button,
		'upload-uploader': Uploader,
		'upload-unsplash': Unsplash,
		'upload-orderable': Orderable,
		'upload-footer': Footer,
		'upload-header': Header,
		'upload-box': Box,
		'upload-thumbnail': Thumbnail,
		'upload-heading': Heading,
		'upload-grid': Grid,
		'error-message': ErrorMessage,
	},

	props: {
		uploader: {
			type: Boolean,
			required: false,
			default: true,
		},

		orderable: {
			type: Boolean,
			required: false,
			default: true,
		},

		unsplash: {
			type: Boolean,
			required: false,
			default: true,
		},

		unsplashAppId: {
			type: String,
			required: false,
			default: null,
		},

		unsplashSecret: {
			type: String,
			required: false,
			default: null,
		},

		api: {
			type: String,
			required: true,
		},

		selection: {
			type: Array,
			required: true,
		},

		quantity: {
			type: Number,
			required: false,
			default: null,
		},

		cropRatio: {
			type: String,
			required: false,
			default: null,
		},

		medias: {
			type: Array,
			required: true,
		},

		maximumSize: {
			type: Number,
			required: true,
		},

		minimumWidth: {
			type: Number,
			required: true,
		},

		minimumHeight: {
			type: Number,
			required: true,
		},

		extensions: {
			type: Array,
			required: true,
		},

		errors: {
			type: Object,
			default: () => {},
		},

		minimumFiles: {
			type: Number,
			required: false,
			default: 0,
		},

		maximumFiles: {
			type: Number,
			required: false,
			default: 0,
		},

		openedTab: {
			type: String,
			required: false,
			default: null,
		},

		lastSearchTerm: {
			type: String,
			required: false,
			default: null,
		},
	},

	data() {
		return {
			limit: 15,
		};
	},

	computed: {
		visibleMedias() {
			return this.orderedMedias.slice(0, this.limit);
		},

		orderedMedias() {
			return _orderBy(this.medias, ['selected'], ['desc']);
		},

		quantityLabel() {
			return this.quantity === 1 ? 'single' : 'multiple';
		},

		ratioLabel() {
			return this.cropRatio !== null ? '-ratio' : '';
		},

		galleryTitle() {
			const text = this.$t(`upload.image-manager.gallery.title.${this.quantityLabel}${this.ratioLabel}`, [this.cropRatio]);

			if (this.quantity > 1) {
				const countdown = `(${this.selection.length}/${this.quantity})`;
				return `${text} ${countdown}`;
			}

			return text;
		},
	},

	watch: {
		errors() {
			if ('hasErrors' in this.errors) {
				this.$forceUpdate();
			}
		},
	},

	mounted() {
		this.updateGrid();
	},

	methods: {
		/* eslint-disable-next-line */
		debouncedScroll: _debounce(function (newValue) {
			this.updateGrid();
		}, 15),

		updateGrid() {
			if (this.$refs.grid) {
				const grid = this.$refs.grid.$el;
				if (grid.scrollTop > (grid.scrollHeight - (grid.offsetHeight + 150))) {
					this.limit += 9;
				}
			}
		},

		handleErrors(value) {
			this.$emit('hasError', value);
		},

		canBeSelected() {
			if (this.quantity) {
				return !this.quantity || this.selection.length < this.quantity || this.quantity === 1;
			}

			if (!this.quantity && this.maximumFiles) {
				return this.selection.length < this.maximumFiles;
			}

			return true;
		},

		canBeSaved() {
			if (this.quantity) {
				return this.quantity && this.selection.length < this.quantity;
			}

			if (!this.quantity && this.minimumFiles) {
				return this.selection.length < this.minimumFiles;
			}

			return false;
		},

		changedTab(tab) {
			this.$emit('hasError', {});

			if (tab.$attrs.id) {
				this.$emit('changed-tab', tab.$attrs.id);
			}
		},
	},
};
</script>
